import { Link } from "react-router-dom";
import { Logo } from "@/components/layout/Logo";
import { Twitter, Linkedin, Github, ArrowUpRight } from "lucide-react";

const columns = [
    {
        title: "Products",
        items: [
            { l: "Bank Statement Analysis", to: "/products/bsa", live: true },
            { l: "Credit Bureau Analysis", to: "#" },
            { l: "Invoice Intelligence", to: "#" },
            { l: "Underwriting Workspace", to: "#" },
        ],
    },
    {
        title: "Company",
        items: [
            { l: "About", to: "/about" },
            { l: "Pricing", to: "/pricing" },
            { l: "Customers", to: "#" },
            { l: "Careers", to: "#", badge: "We're hiring" },
        ],
    },
    {
        title: "Resources",
        items: [
            { l: "Documentation", to: "#" },
            { l: "API reference", to: "#" },
            { l: "Changelog", to: "#" },
            { l: "Status", to: "#" },
        ],
    },
    {
        title: "Legal",
        items: [
            { l: "Privacy", to: "#" },
            { l: "Terms", to: "#" },
            { l: "Security", to: "#" },
            { l: "DPA", to: "#" },
        ],
    },
];

export default function Footer() {
    return (
        <footer className="relative bg-surface-deep text-surface-deep-fg overflow-hidden">
            {/* Subtle grain & decorative gradient */}
            <div className="absolute inset-0 grain pointer-events-none opacity-40" />
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-20 w-[360px] h-[360px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

            <div className="relative container max-w-[1240px] mx-auto px-6 pt-20 pb-10">
                {/* Editorial wordmark */}
                <div className="grid lg:grid-cols-12 gap-10 pb-14">
                    <div className="lg:col-span-4">
                        {/* Force dark-mode CSS variables for the logo so its C-mark
                            (which uses --primary) renders in lime against the
                            always-dark Footer surface, even when the site is in light mode. */}
                        <div className="dark inline-block">
                            <Logo />
                        </div>
                        <p className="mt-5 text-[14.5px] text-surface-deep-fg/65 leading-relaxed max-w-sm">
                            The financial intelligence layer for Southeast Asia&apos;s lenders. Process documents, onboard borrowers, underwrite faster.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-3 text-[12.5px] text-surface-deep-fg/60">
                            <span className="inline-flex w-2 h-2 rounded-full bg-accent animate-pulse-soft" />
                            All systems operational
                        </div>
                    </div>

                    <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                        {columns.map((col) => (
                            <div key={col.title}>
                                <h4 className="font-serif text-[13px] tracking-wider uppercase text-surface-deep-fg/55 mb-4">{col.title}</h4>
                                <ul className="flex flex-col gap-3">
                                    {col.items.map((it) => (
                                        <li key={it.l}>
                                            <Link
                                                to={it.to}
                                                className="text-[14px] text-surface-deep-fg/80 hover:text-accent transition-colors inline-flex items-center gap-2"
                                            >
                                                {it.l}
                                                {it.live && <span className="text-[9px] tracking-widest uppercase font-semibold px-1.5 py-0.5 rounded bg-accent/20 text-accent">Live</span>}
                                                {it.badge && <span className="text-[10px] tracking-wider font-medium px-1.5 py-0.5 rounded bg-gold/20 text-gold">{it.badge}</span>}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Massive editorial wordmark */}
                <div className="py-6 border-t border-surface-deep-fg/10">
                    <p className="font-serif text-[18vw] md:text-[14vw] leading-[0.9] tracking-[-0.04em] text-surface-deep-fg/[0.06] select-none">
                        CredNX
                    </p>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6 border-t border-surface-deep-fg/10">
                    <p className="text-[12.5px] text-surface-deep-fg/55">
                        © {new Date().getFullYear()} CredNX Pte Ltd. Crafted in Singapore & Jakarta.
                    </p>
                    <div className="flex items-center gap-3">
                        {[Twitter, Linkedin, Github].map((Icon, i) => (
                            <a
                                key={i}
                                href="#"
                                className="w-9 h-9 rounded-full border border-surface-deep-fg/15 inline-flex items-center justify-center text-surface-deep-fg/70 hover:text-accent hover:border-accent/40 transition-colors"
                                aria-label="Social"
                            >
                                <Icon className="w-[15px] h-[15px]" />
                            </a>
                        ))}
                        <Link to="/contact" className="ml-2 inline-flex items-center gap-1.5 text-[13px] text-accent hover:text-accent-glow font-medium">
                            Talk to us <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
